// @owners { team: patients-team }
import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoSpinningLoader,
  ButtonSelector,
  Card,
  type DateData,
  Description,
  Divider,
  H2,
  Icon,
  InputRadioGroup,
  LgPadding,
  LgSpacing,
  ListHeader,
  ListItem,
  type OptionValue,
  Row,
  SmSpacing,
} from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { type ShipmentFeeType } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_type';
import {
  type FulfillmentOption,
  FulfillmentOptionTagMap,
  type FulfillmentWindowOption,
} from '@alto/scriptdash/alto/patient_app/checkout/v1/checkout_endpoint';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { updateOrder } from '~shared/actions/checkout';
import { getAddress } from '~shared/features/addresses/selectors/getAddress';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { useCheckoutDateErrors } from '~shared/features/checkout/hooks/useCheckoutDateErrors';
import { usePrescriptionIDsForCurrentOrder } from '~shared/features/checkout/hooks/usePrescriptionIDsForCurrentOrder';
import {
  getEditShipmentID,
  getShowingValidationErrors,
  getWindows,
} from '~shared/features/checkout/selectors/getCheckout';
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import {
  getFirstOrderDate,
  getIsOrderPickupMethod,
  getOrderAddressId,
  getOrderDate,
  getOrderDeliverAfter,
  getOrderDeliverBefore,
  getOrderIsAsap,
} from '~shared/features/checkout/selectors/getOrder';
import { getOrderIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import getSelectedPaymentTypes from '~shared/features/checkout/selectors/getSelectedPaymentTypes';
import { getShipmentPaymentMethodsByShipmentID } from '~shared/features/payments/selectors/getShipmentPaymentMethods';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { getIsGskForYouUser } from '~shared/features/users/selectors/getIsGskForYouUser';
import { formatDateStringForAPI, formatWindowTime } from '~shared/helpers/date';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { isCourier, isMail } from '~shared/helpers/order';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useGetPaymentMethod } from '~shared/queries/query-keys/paymentMethods';
import { exitOrderBundling, initializeOrderBundling } from '~shared/reducers/ui/checkoutFlow';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import {
  SelectDeliveryWindowActionSheet,
  getOrderBundlingCheckoutOrigin,
  useOrderBundlingOptions,
  useQueryFulfillmentOptions,
} from '../../../..';
import { getSelectedPriceTypesForPrescriptions } from '../../helpers/getSelectedPriceTypesForPrescriptions';
import { AddressInfo } from '../delivery-address-card/address-info/AddressInfo';
import { AddressInfoAlert } from '../delivery-address-card/address-info/AddressInfoAlert';
import { FeeRadioRightContent } from '../select-date-card/FeeRadioRightContent';
import { FeeDetails } from './FeeDetails';
import { FulfillmentOptionRadioOption } from './FulfillmentOptionRadioOption';
import { PickupInfo } from './PickupInfo';
import { PickupOptionsSection } from './PickupOptionsSection';
import { SelectCustomDateActionSheet } from './SelectCustomDateActionSheet';
import { createCustomFulfillmentOption, getRadioDateOptions, initSelectedFulfillmentWindowOption } from './utils';

type Props = {
  readonly openGetItSoonerFlow: () => void;
};

export const DELIVERY_VALUE = 'Delivery';
export const PICKUP_VALUE = 'Pickup';
export type DeliveryMethodToggleOptions = typeof DELIVERY_VALUE | typeof PICKUP_VALUE;

const pickupToggleOptions = [
  { label: DELIVERY_VALUE, value: DELIVERY_VALUE, testID: 'checkout-delivery' },
  { label: PICKUP_VALUE, value: PICKUP_VALUE, testID: 'checkout-pickup' },
];

const isWeb = getPlatformOS() === 'web';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const SchedulingCard = ({ openGetItSoonerFlow }: Props) => {
  const dispatch = useDispatchShared();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackEvent } = useAnalytics();
  const { value: isPickupToggleEnabled } = Experimentation.useFeatureFlag('scheduling_module_pickup_toggle');
  const { value: isNextDayMailEnabled } = Experimentation.useFeatureFlag('next_day_mail');
  const { value: isGskForYouEnabled } = Experimentation.useFeatureFlag('patients_gsk');

  // order selectors
  const orderDate = useSelectorShared(getOrderDate);
  const isAsap = useSelectorShared(getOrderIsAsap);
  const firstOrderDate = useSelectorShared(getFirstOrderDate);
  const isOrderPickupMethod = useSelectorShared(getIsOrderPickupMethod);
  const orderDeliverAfter = useSelectorShared(getOrderDeliverAfter);
  const orderDeliverBefore = useSelectorShared(getOrderDeliverBefore);
  const addressID = useSelectorShared(getOrderAddressId);
  const address = useSelectorShared((state) => getAddress(state, { addressID }));
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  // State for the selected date radio option
  const getInitialCheckedValue = (): string | null => {
    if (!orderDate) return null;

    if (isOrderPickupMethod) {
      return `PICKUP-${orderDate}`;
    }

    if (isOrderBundling) {
      return `${orderDate}-${FulfillmentOptionTagMap.BUNDLED}`;
    }
    return orderDate;
  };
  const [checkedValue, setCheckedValue] = useState<string | null>(getInitialCheckedValue());
  // State for any custom date chosen from "Select other date". Constructed only on the FE.
  const [customFulfillmentOption, setCustomFulfillmentOption] = useState<FulfillmentOption | undefined>(undefined);
  // State for the selected fulfillment window option.
  const [selectedFulfillmentWindowOption, setSelectedFulfillmentWindowOption] = useState<
    FulfillmentWindowOption | undefined
  >(initSelectedFulfillmentWindowOption({ orderDate, orderDeliverAfter, orderDeliverBefore, isAsap }));

  // other selectors
  const isGskForYouUser = useSelectorShared(getIsGskForYouUser);
  const saveOrderLoading = useSelectorShared((state) => state.ui.loading.saveOrderLoading);
  const showingValidationErrors = useSelectorShared(getShowingValidationErrors);
  const { dateValidationErrors } = useCheckoutDateErrors();
  const { prescriptionIDs: cartPrescriptionIDs } = usePrescriptionsInCart();
  const cartSelectedPaymentTypes = useSelectorShared(getSelectedPaymentTypes);
  const selectedPrices = useMemo(
    () =>
      getSelectedPriceTypesForPrescriptions({
        prescriptionIDs: cartPrescriptionIDs,
        cartSelectedPaymentTypes,
      }),
    [cartPrescriptionIDs, cartSelectedPaymentTypes],
  );
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const orderBundlingOption = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const { earliestUpcomingShipment } = useOrderBundlingOptions({
    address_id: addressID,
    origin: orderBundlingOption,
  });
  const earliestUpcomingShipmentPaymentMethods = useSelectorShared((state) =>
    getShipmentPaymentMethodsByShipmentID(state, { shipmentID: earliestUpcomingShipment?.id }),
  );
  const earliestUpcomingShipmentPaymentMethodId = earliestUpcomingShipmentPaymentMethods[0]?.payment_method_id;
  const earliestUpcomingShipmentSecondPaymentMethodId = earliestUpcomingShipmentPaymentMethods[1]?.payment_method_id;
  const isEarliestUpcomingShipmentPaymentMethodIdApplePay =
    useGetPaymentMethod({
      paymentMethodID: earliestUpcomingShipmentPaymentMethodId,
      enabled: !!earliestUpcomingShipmentPaymentMethodId,
    })?.paymentMethod?.details?.native_pay_type === 'apple_pay';
  const editShipmentId = useSelectorShared(getEditShipmentID);

  const { deliveryMethods } = useCartDeliveryMethods();
  const orderIndex = useSelectorShared(getOrderIndex);
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];
  const isFirstShipment = orderIndex === 0;
  const prescriptionIDsForCurrentOrder = usePrescriptionIDsForCurrentOrder();

  const selectedPricesForCurrentOrder = useMemo(() => {
    if (deliveryMethods.length <= 1) return selectedPrices;
    return (selectedPrices ?? []).filter((p) => prescriptionIDsForCurrentOrder.includes(p.prescription_id));
  }, [deliveryMethods, selectedPrices, prescriptionIDsForCurrentOrder]);

  const isMailOrder = isMail(deliveryMethod?.mode);
  const isCourierOrder = isCourier(deliveryMethod?.mode);
  // Enable mail options for gsk_for_you users before next_day mail fee is fully enabled
  const isMailOptionsEnabled = (isNextDayMailEnabled || (isGskForYouEnabled && isGskForYouUser)) && isMailOrder;

  const {
    courierDateFulfillmentOptions,
    courierAnyDateFulfillmentOption,
    mailFulfillmentOptions,
    mailAnyDateFulfillmentOption,
    pickupFulfillmentOptions,
    isLoading: isFulfillmentOptionsLoading,
    isSuccess: isFulfillmentOptionsSuccess,
  } = useQueryFulfillmentOptions({
    addressID,
    cartPrescriptionIDs: prescriptionIDsForCurrentOrder,
    selectedPrices: selectedPricesForCurrentOrder,
    shipmentID: editShipmentId,
  });
  const hasAvailablePickupOptions = pickupFulfillmentOptions.length > 0;
  const showPickupToggle = isPickupToggleEnabled && hasAvailablePickupOptions;
  const showDeliveryOptions = !isPickupToggleEnabled || !isOrderPickupMethod || !showPickupToggle;
  const ready_by_time = pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time;
  const orderBundlingDates = courierDateFulfillmentOptions
    .filter((option) => option.tag === FulfillmentOptionTagMap.BUNDLED)
    .map((option) => option.date)
    .filter(TypescriptUtils.isPresent);

  // Get any applicable fees for any day case - used when selecting custom dates
  const anyDateOption = isMailOptionsEnabled ? mailAnyDateFulfillmentOption : courierAnyDateFulfillmentOption;

  // Show Fee Details link when gsk_for_you user has next day mail fees in cart
  const fulfillmentOptionFees = [...mailFulfillmentOptions, mailAnyDateFulfillmentOption].flatMap(
    (fulfillmentOption) => fulfillmentOption?.available_fees,
  );
  const showFeeDetails =
    isGskForYouUser && fulfillmentOptionFees.some((fee) => fee?.fee_type === 'next_day_mail' && fee.amount > 0);
  const disableSelector = saveOrderLoading || isFulfillmentOptionsLoading;
  const showErrors = showingValidationErrors && dateValidationErrors?.length;
  const errorMessage = dateValidationErrors.map((e) => e.message).join('. ');

  let cardTitle: undefined | string = undefined;

  // Scheduling card title will be 'Schedule delivery' when pickup toggle is not enabled
  // When pickup toggle is enabled:
  //  1. Card title will be 'Select delivery date' if NO pickup options are available
  //  2. When pickup options are available, a Header will be displayed under the toggle selector to replace the card title
  if (!isPickupToggleEnabled) {
    cardTitle = 'Schedule delivery';
  } else if (!hasAvailablePickupOptions) {
    cardTitle = 'Select delivery date';
  }

  // if pickup toggle was enabled and then the app was backgrounded, check if pickup options are still available when the app is foregrounded
  // if not, reset order state
  useEffect(() => {
    if (!hasAvailablePickupOptions && showPickupToggle && !isFulfillmentOptionsLoading) {
      dispatch(
        updateOrder({
          date: undefined,
          delivery_method: undefined,
          pickup_address: undefined,
          pickup_ready_time: undefined,
          pay_at_pickup: undefined,
          home_to_sign_confirmation: undefined,
          deliver_after: undefined,
          deliver_before: undefined,
        }),
      );
    }
  }, [hasAvailablePickupOptions, showPickupToggle, dispatch, isFulfillmentOptionsLoading]);

  useEffect(() => {
    if (!isFulfillmentOptionsSuccess) return;

    const asapFulfillmentOption = courierDateFulfillmentOptions.find((fulfillmentOption) => {
      return fulfillmentOption.available_fees.some((fee) => fee.fee_type === 'asap_delivery');
    });

    trackEvent({
      event: EVENTS.CHECKOUT_VIEWED_WITH_FULFILLMENT_OPTIONS,
      params: {
        origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
        shipment_id: isEditingOrder ? editShipmentId : null,
        asap_eligible: !!asapFulfillmentOption,
        // only populated when one courier window option for asap delivery
        asap_eta: asapFulfillmentOption?.fulfillment_window_option?.estimated_eta,
        pickup_eligible: pickupFulfillmentOptions.length > 0,
        pickup_eta: pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time,
      },
    });
  }, [
    trackEvent,
    isFulfillmentOptionsSuccess,
    courierDateFulfillmentOptions,
    isEditingOrder,
    editShipmentId,
    pickupFulfillmentOptions,
  ]);

  const onMountRef = useRef(true);
  useEffect(() => {
    // this effect should only run when the address changes, not on mount
    // i.e: in edit order flow, without this, we would not preselect the order's date in InputRadioGroup
    if (onMountRef.current) {
      onMountRef.current = false;
      return;
    }

    // Whenever address changes or address safe place changes clear out
    // any existing state selections as they may not be applicable.
    setCustomFulfillmentOption(undefined);
    setSelectedFulfillmentWindowOption(undefined);
    setCheckedValue(null);
  }, [addressID, address?.safe_place]);

  // Whenever the checkedValue is a pickup value and the pickup_ready_time changes, update the pickup_ready_time in redux
  useEffect(() => {
    if (!checkedValue?.startsWith('PICKUP')) return;

    dispatch(updateOrder({ pickup_ready_time: ready_by_time }));
  }, [checkedValue, dispatch, ready_by_time]);

  // When moving to the second (or later) order in a split shipment, reset any custom fulfillment option
  // from the first order so we don't carry over a pre-selected custom date/window.
  useEffect(() => {
    if (orderIndex > 0) {
      setCustomFulfillmentOption(undefined);
      setSelectedFulfillmentWindowOption(undefined);
    }
  }, [orderIndex]);

  // When entering the second order of a split shipment, pre-fill date from first order only if second order has no date yet.
  // Do not overwrite when the user has already selected a date (e.g. a mail date different from first order).
  // Set delivery_method for mail so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
  useEffect(() => {
    if (orderIndex > 0 && firstOrderDate && !orderDate) {
      const formatted = formatDateStringForAPI(firstOrderDate);
      setCheckedValue(formatted);
      const updatePayload =
        orderIndex === 1 && isMailOrder
          ? { date: firstOrderDate, delivery_method: deliveryMethod?.name }
          : { date: firstOrderDate };
      dispatch(updateOrder(updatePayload));
    }
  }, [deliveryMethod?.name, dispatch, firstOrderDate, isMailOrder, orderIndex, orderDate]);

  // Once the second order date is set, auto-select the first delivery window after windows are fetched
  // via cart middleware and in the redux store
  const windows = useSelectorShared(getWindows);
  const deliver_after = windows[0]?.deliver_after;
  const deliver_before = windows[0]?.deliver_before;
  useEffect(() => {
    if (orderIndex > 0 && isCourierOrder && deliver_after && deliver_before) {
      dispatch(updateOrder({ deliver_after, deliver_before }));
    }
  }, [deliver_after, deliver_before, dispatch, isCourierOrder, orderIndex]);

  useEffect(() => {
    if (orderIndex > 0 && orderDate && orderDeliverAfter && orderDeliverBefore && !selectedFulfillmentWindowOption) {
      const window = initSelectedFulfillmentWindowOption({ orderDate, orderDeliverAfter, orderDeliverBefore });
      setSelectedFulfillmentWindowOption(window);
    }
  }, [orderDate, orderDeliverAfter, orderDeliverBefore, orderIndex, selectedFulfillmentWindowOption]);

  // When the order date is set but not in the list of available options, create a custom fulfillment option
  // so the radio can display the selected date. Applies to first order (custom date + window) and second order
  // in split shipment (date from first order; courier has a window, mail does not).
  useEffect(() => {
    if (isFulfillmentOptionsLoading || !isFulfillmentOptionsSuccess || !orderDate) return;

    let baseOptions = courierDateFulfillmentOptions;
    if (isMailOrder) {
      baseOptions = isMailOptionsEnabled ? mailFulfillmentOptions : courierDateFulfillmentOptions;
    }

    const selectedOption = baseOptions.find((option) => option?.date === orderDate);
    if (selectedOption) return;

    const { available_fees, fee_amount_label, fee_original_amount_label } = anyDateOption || {};
    const feeOverrides = {
      available_fees: available_fees ?? [],
      fee_amount_label,
      fee_original_amount_label,
    };

    // Courier: use selected fulfillment window when present (first order custom date or second order with window).
    if (selectedFulfillmentWindowOption && orderDeliverAfter && orderDeliverBefore) {
      setCustomFulfillmentOption({
        ...createCustomFulfillmentOption(orderDate, selectedFulfillmentWindowOption),
        ...feeOverrides,
      });
      return;
    }

    // Mail or no window: date-only custom option (e.g. second order mail, or before window is set).
    setCustomFulfillmentOption({
      ...createCustomFulfillmentOption(orderDate),
      ...feeOverrides,
    });
  }, [
    anyDateOption,
    courierDateFulfillmentOptions,
    isFulfillmentOptionsLoading,
    isFulfillmentOptionsSuccess,
    isMailOptionsEnabled,
    isMailOrder,
    mailFulfillmentOptions,
    orderDate,
    orderDeliverAfter,
    orderDeliverBefore,
    orderIndex,
    selectedFulfillmentWindowOption,
  ]);

  // if date options do not have fees, no order bundling option, and one window available,
  // auto-select the earliest date option when entering checkout and set the order date and window
  useEffect(() => {
    // If a date is already set or pickup is selected, do not pre-select a date.
    if (checkedValue || isOrderPickupMethod) return;

    const dateOptions = isMailOptionsEnabled ? mailFulfillmentOptions : courierDateFulfillmentOptions;

    const hasFees = dateOptions.some((option) => option.available_fees.length > 0);
    const hasOrderBundling = dateOptions.some((option) => option.tag === 'BUNDLED');
    if (hasFees || hasOrderBundling) return;

    const earliestOption = dateOptions[0];
    if (!earliestOption) return;
    // when there's only one window it will be populated in the fulfillment_window_option
    const { date, fulfillment_window_option: option } = earliestOption;
    if (!date) return;
    const formattedDate = formatDateStringForAPI(date);

    // If the delivery method is mail and no pickup option is selected, default to the first date available.
    // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
    if (isMailOrder && !isOrderPickupMethod) {
      dispatch(updateOrder({ date: formattedDate, delivery_method: deliveryMethod?.name }));
      setCheckedValue(formattedDate);
      return;
    }

    // Only pre-select if a window option exists for that date.
    if (!option?.delivery_window) return;

    const { start_time, end_time } = option.delivery_window;
    dispatch(
      updateOrder({
        date: formattedDate,
        deliver_after: start_time,
        deliver_before: end_time,
        delivery_method: undefined,
      }),
    );
    setCheckedValue(formattedDate);
  }, [
    checkedValue,
    courierDateFulfillmentOptions,
    deliveryMethod?.mode,
    deliveryMethod?.name,
    dispatch,
    isMailOptionsEnabled,
    isMailOrder,
    isOrderPickupMethod,
    mailFulfillmentOptions,
  ]);

  const trackWindowSelected = useCallback(
    ({
      autoSelected,
      deliver_after,
      deliver_before,
      fee_type,
    }: {
      autoSelected?: boolean;
      deliver_after?: string;
      deliver_before?: string;
      fee_type?: ShipmentFeeType | null;
    }) => {
      trackEvent({
        event: EVENTS.CHECKOUT__WINDOW_SELECTED,
        params: {
          'auto-selected': autoSelected ?? false,
          origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
          deliver_after,
          deliver_before,
          fee_type,
        },
      });
    },
    [isEditingOrder, trackEvent],
  );

  const handleCustomDateDeliveryWindowSelection = useCallback(
    (deliveryDate: string, deliveryWindow: FulfillmentWindowOption) => {
      const customFulfillmentOption = createCustomFulfillmentOption(deliveryDate, deliveryWindow);
      setCustomFulfillmentOption(customFulfillmentOption);
      setCheckedValue(formatDateStringForAPI(deliveryDate));
      setSelectedFulfillmentWindowOption(deliveryWindow);

      const deliver_after = deliveryWindow.delivery_window?.start_time;
      const deliver_before = deliveryWindow.delivery_window?.end_time;
      const isAsap = deliveryWindow.available_fee?.fee_type === 'asap_delivery';

      trackWindowSelected({ deliver_after, deliver_before, fee_type: deliveryWindow.available_fee?.fee_type });

      dispatch(
        updateOrder({
          date: deliveryDate,
          deliver_after,
          deliver_before,
          delivery_method: deliveryMethod?.name || null,
          is_asap: isAsap,
        }),
      );
      // If the user selects any other date, then it cannot be an order bundling
      // option, so we clear any previous order bundling state
      dispatch(exitOrderBundling());
      closeActionSheet();
    },
    [closeActionSheet, deliveryMethod?.name, dispatch, trackWindowSelected],
  );

  const handleCustomDateSelection = useCallback(
    (date: DateData) => {
      const deliveryDate = formatDateStringForAPI(new Date(date.timestamp).toISOString());

      // If delivery method is mail, then delivery windows are not
      // applicable. Set order date and do not show window selection modal.
      if (isMailOrder) {
        closeActionSheet();
        // Get any applicable fees for any day case when selecting a custom date for mail delivery
        const { available_fees, fee_amount_label, fee_original_amount_label } = anyDateOption || {};

        const customFulfillmentOption = {
          ...createCustomFulfillmentOption(deliveryDate),
          available_fees,
          fee_amount_label,
          fee_original_amount_label,
        } as FulfillmentOption;
        setCustomFulfillmentOption(customFulfillmentOption);
        setCheckedValue(deliveryDate);
        // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
        dispatch(updateOrder({ date: deliveryDate, delivery_method: deliveryMethod?.name }));
        return;
      }

      setActiveActionSheet(
        <SelectDeliveryWindowActionSheet
          deliveryDate={deliveryDate}
          selectedWindowOption={selectedFulfillmentWindowOption}
          handleCustomDateDeliveryWindowSelection={handleCustomDateDeliveryWindowSelection}
        />,
      );
    },
    [
      anyDateOption,
      closeActionSheet,
      deliveryMethod?.name,
      dispatch,
      handleCustomDateDeliveryWindowSelection,
      isMailOrder,
      setActiveActionSheet,
      selectedFulfillmentWindowOption,
    ],
  );

  const handleStandardDeliveryWindowSelection = useCallback(
    (deliveryWindow: FulfillmentWindowOption) => {
      const isAsap = deliveryWindow?.available_fee?.fee_type === 'asap_delivery';
      let deliver_after: string | undefined;
      let deliver_before: string | undefined;
      if (isAsap) {
        deliver_after = formatWindowTime({
          windowTime: new Date(),
          meridiem: 'a',
          forceReturnMinutes: true,
          useUTC: false,
          separator: ' ',
        });
        deliver_before = formatWindowTime({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          windowTime: deliveryWindow.estimated_eta!,
          meridiem: 'a',
          forceReturnMinutes: true,
          useUTC: false,
          separator: ' ',
        });
      } else {
        deliver_after = deliveryWindow.delivery_window?.start_time;
        deliver_before = deliveryWindow.delivery_window?.end_time;
      }

      trackWindowSelected({ deliver_after, deliver_before, fee_type: deliveryWindow.available_fee?.fee_type });

      setCustomFulfillmentOption(undefined);
      setSelectedFulfillmentWindowOption(deliveryWindow);
      setCheckedValue(formatDateStringForAPI(deliveryWindow.date));
      dispatch(
        updateOrder({
          date: formatDateStringForAPI(deliveryWindow.date),
          deliver_before,
          deliver_after,
          delivery_method: deliveryMethod?.name || null,
          is_asap: isAsap,
        }),
      );
      closeActionSheet();
    },
    [closeActionSheet, deliveryMethod?.name, dispatch, trackWindowSelected],
  );

  const setSelectedDateWithoutWindow = (dateKey: string) => {
    setSelectedFulfillmentWindowOption(undefined);
    setCustomFulfillmentOption(undefined);
    setCheckedValue(dateKey);
  };

  const selectPickUpOption = useCallback(
    (dateKey: string) => {
      const selectedDate = dateKey.replace('PICKUP-', '');
      const selectedOption = pickupFulfillmentOptions[0];

      trackEvent({
        event: EVENTS.CHECKOUT__PICKUP_SELECTED,
        params: {
          origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
          ready_by_time: selectedOption?.pickup_data?.ready_by_time,
        },
      });
      setSelectedDateWithoutWindow(dateKey);
      dispatch(
        updateOrder({
          date: selectedDate,
          tip_amount: undefined,
          delivery_method: 'pickup',
          deliver_after: undefined,
          deliver_before: undefined,
          pickup_address: pickupFulfillmentOptions[0]?.pickup_data?.facility_address,
          pickup_ready_time: pickupFulfillmentOptions[0]?.pickup_data?.ready_by_time,
        }),
      );
    },
    [dispatch, isEditingOrder, pickupFulfillmentOptions, trackEvent],
  );

  const selectBundledOption = useCallback(
    (dateKey: string, selectedOption: FulfillmentOption) => {
      const selectedDate = dateKey.replace(`-${FulfillmentOptionTagMap.BUNDLED}`, '');
      setSelectedDateWithoutWindow(dateKey);
      dispatch(initializeOrderBundling());

      const { start_time, end_time } = selectedOption?.fulfillment_window_option?.delivery_window ?? {};
      dispatch(
        updateOrder({
          date: selectedDate,
          deliver_after: start_time,
          deliver_before: end_time,
          tip_amount: undefined,
          delivery_method: deliveryMethod?.name || null,
          pay_with_apple_pay: isEarliestUpcomingShipmentPaymentMethodIdApplePay,
          payment_method_id: earliestUpcomingShipmentPaymentMethodId,
          second_payment_method_id: earliestUpcomingShipmentSecondPaymentMethodId,
        }),
      );
    },
    [
      deliveryMethod?.name,
      dispatch,
      earliestUpcomingShipmentPaymentMethodId,
      earliestUpcomingShipmentSecondPaymentMethodId,
      isEarliestUpcomingShipmentPaymentMethodIdApplePay,
    ],
  );

  const handleRadioPress = useCallback(
    (dateKey: string) => {
      // It is possible to have multiple options with the same date if one of them is
      // bundled and the other is not. To account for this we structure the date key
      // to be MM-DD-YYYY and append -${tag} if a tag (like bundled) is applicable.
      const selectedDate = dateKey;

      const isPickupOption = dateKey.startsWith('PICKUP');
      if (isPickupOption) {
        selectPickUpOption(dateKey);
        return;
      }

      // Clearing delivery method when switching to a different non-pickup option would cause pricing to use 'courier' default.
      // We keep setting delivery_method from the selected option below so pricing returns correct fees.
      const selectedOption = [
        ...courierDateFulfillmentOptions,
        ...mailFulfillmentOptions,
        customFulfillmentOption,
      ].find((option) => {
        const tagSuffix = option?.tag ? `-${option.tag}` : '';
        return `${option?.date}${tagSuffix}` === dateKey;
      });

      const isBundledOption = dateKey.includes(FulfillmentOptionTagMap.BUNDLED);
      if (isBundledOption && selectedOption) {
        selectBundledOption(dateKey, selectedOption);
        return;
      } else {
        // Exit order bundling state if user selected a non bundled option.
        dispatch(exitOrderBundling());
      }

      // If delivery method is mail, then delivery windows are not
      // applicable. Set order date and do not show window selection modal.
      if (isMailOrder) {
        if (selectedDate !== customFulfillmentOption?.date) {
          setCustomFulfillmentOption(undefined);
        }
        setSelectedFulfillmentWindowOption(undefined);
        setCheckedValue(dateKey);
        // Set delivery_method so pricing API returns correct fees (e.g. next_day_mail); we only send it to BE when placing for pickup.
        dispatch(updateOrder({ date: selectedDate, delivery_method: deliveryMethod?.name }));

        return;
      }

      // If there is only a single fulfillment option for the date auto-select it
      if (selectedOption?.fulfillment_window_option?.delivery_window) {
        setSelectedFulfillmentWindowOption(undefined);
        const { delivery_window: window, available_fee } = selectedOption.fulfillment_window_option;
        const isAsap = available_fee?.fee_type === 'asap_delivery';
        setCheckedValue(dateKey);

        if (selectedDate !== customFulfillmentOption?.date) {
          setCustomFulfillmentOption(undefined);
        }

        trackWindowSelected({
          autoSelected: true,
          deliver_after: window.start_time,
          deliver_before: window.end_time,
          fee_type: available_fee?.fee_type,
        });

        dispatch(updateOrder({ date: selectedDate, is_asap: isAsap }));
        dispatch(
          updateOrder({
            deliver_after: window.start_time,
            deliver_before: window.end_time,
            delivery_method: deliveryMethod?.name || null,
          }),
        );

        // Only clear bundling once a date has been selected
        if (selectedOption.tag !== 'BUNDLED') {
          dispatch(exitOrderBundling());
        }
        return;
      }

      // If not bundled and not pickup, show the action sheet to select a delivery window from the selected date.
      if (!isBundledOption && !isPickupOption) {
        setActiveActionSheet(
          <SelectDeliveryWindowActionSheet
            deliveryDate={selectedDate}
            selectedWindowOption={selectedFulfillmentWindowOption}
            handleDeliveryWindowSelection={handleStandardDeliveryWindowSelection}
          />,
        );
      }
    },
    [
      courierDateFulfillmentOptions,
      mailFulfillmentOptions,
      customFulfillmentOption,
      deliveryMethod?.name,
      dispatch,
      handleStandardDeliveryWindowSelection,
      isMailOrder,
      selectBundledOption,
      selectPickUpOption,
      selectedFulfillmentWindowOption,
      setActiveActionSheet,
      trackWindowSelected,
    ],
  );

  const handleCheckoutPickupToggle = useCallback(
    (value: OptionValue) => {
      // Toggle to delivery
      if (value !== PICKUP_VALUE) {
        trackEvent({
          event: EVENTS.CHECKOUT__DELIVERY_TOGGLED,
          params: {
            origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
          },
        });
        setCheckedValue(null);
        dispatch(
          updateOrder({
            date: undefined,
            delivery_method: deliveryMethod?.name || null,
            pickup_address: undefined,
            pickup_ready_time: undefined,
            pay_at_pickup: undefined,
            home_to_sign_confirmation: undefined,
            deliver_after: undefined,
            deliver_before: undefined,
          }),
        );
        return;
      }

      // Toggle to pickup
      // Display and directly select the same-day pickup option since it's the only pickup option available for now
      const selectedSameDayPickupOption = pickupFulfillmentOptions[0];
      trackEvent({
        event: EVENTS.CHECKOUT__PICKUP_TOGGLED,
        params: {
          origin: isEditingOrder ? ORIGIN_NAMES.EDIT_DELIVERY : ORIGIN_NAMES.CHECKOUT,
          ready_by_time: selectedSameDayPickupOption?.pickup_data?.ready_by_time,
        },
      });
      if (!selectedSameDayPickupOption) {
        return;
      }

      setCheckedValue(null);
      setSelectedFulfillmentWindowOption(undefined);
      setCustomFulfillmentOption(undefined);

      if (!selectedSameDayPickupOption.date) {
        return;
      }
      dispatch(
        updateOrder({
          date: selectedSameDayPickupOption.date,
          delivery_method: 'pickup',
          pickup_address: selectedSameDayPickupOption.pickup_data?.facility_address,
          pickup_ready_time: selectedSameDayPickupOption.pickup_data?.ready_by_time,
          deliver_after: undefined,
          deliver_before: undefined,
          tip_amount: undefined,
        }),
      );
    },
    [deliveryMethod?.name, dispatch, pickupFulfillmentOptions, isEditingOrder, trackEvent],
  );

  if (isFulfillmentOptionsLoading) {
    return (
      <Card title="Schedule delivery">
        <Row centerHorizontally>
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            <AltoSpinningLoader />
          </LgPadding>
        </Row>
      </Card>
    );
  }

  // When feature flag is ON, mail options are returned separately.
  // This preserves backward compatibility: when feature flag is OFF, mail options come in courierDateFulfillmentOptions
  const radioDateOptions = getRadioDateOptions({
    courierDateFulfillmentOptions,
    mailFulfillmentOptions: isMailOptionsEnabled ? mailFulfillmentOptions : [],
    customFulfillmentOption,
    isSecondSplitShipment: orderIndex > 0,
    selectedDate: orderDate,
    isMailOptionsEnabled,
  });

  return (
    <Card
      title={cardTitle}
      RightContent={showFeeDetails ? <FeeDetails /> : undefined}
    >
      {showPickupToggle ? (
        <>
          <LgPadding bottomPadding={SPACING.STATIC.NONE}>
            <ButtonSelector
              testID="checkout-pickup-selector"
              onChange={handleCheckoutPickupToggle}
              options={pickupToggleOptions}
              value={isOrderPickupMethod ? PICKUP_VALUE : DELIVERY_VALUE}
            />
          </LgPadding>
          {!isOrderPickupMethod ? (
            <LgPadding
              topPadding={isWeb ? SPACING.STATIC.XS : SPACING.STATIC.LG}
              bottomPadding={SPACING.STATIC.MD}
            >
              <ListHeader
                title="Select delivery date"
                fullBleed
              />
            </LgPadding>
          ) : null}
        </>
      ) : null}
      {showDeliveryOptions ? (
        <>
          <InputRadioGroup
            value={checkedValue}
            onValueChange={handleRadioPress}
            disabled={disableSelector}
          >
            {radioDateOptions.map((fulfillmentOption) => {
              return (
                <FulfillmentOptionRadioOption
                  key={`radio-${fulfillmentOption.date}-${fulfillmentOption.tag}-${fulfillmentOption.fee_amount_label}`}
                  fulfillmentOption={fulfillmentOption}
                  disableSelector={disableSelector}
                  loading={isFulfillmentOptionsLoading}
                  selectedWindowOption={selectedFulfillmentWindowOption}
                  deliveryMethod={deliveryMethod}
                  checkedValue={checkedValue}
                />
              );
            })}
          </InputRadioGroup>
          {/* Show "Select other date" only for the first shipment; second shipment cannot change date (single date sent to /v3/deliveries). */}
          {isFirstShipment && anyDateOption ? (
            <React.Fragment key={'any-day-option'}>
              <ListItem
                LeftContent={<Icon name="calendar" />}
                onPress={() => {
                  setActiveActionSheet(
                    <SelectCustomDateActionSheet
                      minDate={anyDateOption?.date || ''}
                      orderBundlingDates={orderBundlingDates}
                      handleDateSelection={handleCustomDateSelection}
                      openGetItSoonerFlow={openGetItSoonerFlow}
                    />,
                  );
                }}
                RightContent={
                  <FeeRadioRightContent
                    feeAmountLabel={anyDateOption?.fee_amount_label || undefined}
                    feeOriginalAmountLabel={anyDateOption?.fee_original_amount_label || undefined}
                    loading={isFulfillmentOptionsLoading}
                    showIconOpenCalendar
                  />
                }
                title={anyDateOption?.label || ''}
                testID={'any-day-delivery'}
              />
            </React.Fragment>
          ) : null}
          <AddressInfo topPadding={SPACING.STATIC.MD} />
          {showErrors ? (
            <AddressInfoAlert
              type="error"
              topPadding={SPACING.STATIC.MD}
              bottomPadding={SPACING.STATIC.MD}
            >
              <Description>{errorMessage}</Description>
            </AddressInfoAlert>
          ) : (
            <LgSpacing />
          )}
        </>
      ) : null}
      {hasAvailablePickupOptions && !isPickupToggleEnabled ? (
        <>
          <Divider />
          <LgPadding
            topPadding={SPACING.STATIC.MD}
            bottomPadding={SPACING.STATIC.MD}
          >
            <H2>Pick up at Alto</H2>
          </LgPadding>
          <InputRadioGroup
            value={checkedValue}
            onValueChange={handleRadioPress}
            disabled={disableSelector}
          >
            {pickupFulfillmentOptions.map((fulfillmentOption) => {
              return (
                <FulfillmentOptionRadioOption
                  key={`radio-${fulfillmentOption.date}-${fulfillmentOption.tag}-${fulfillmentOption.fee_amount_label}`}
                  fulfillmentOption={fulfillmentOption}
                  disableSelector={disableSelector}
                  loading={isFulfillmentOptionsLoading}
                />
              );
            })}
            {checkedValue?.startsWith('PICKUP') ? (
              <PickupInfo
                pickupData={
                  pickupFulfillmentOptions.find((pickupOption) => checkedValue === `PICKUP-${pickupOption.date}`)
                    ?.pickup_data
                }
              />
            ) : null}
          </InputRadioGroup>
          <SmSpacing />
        </>
      ) : null}
      {hasAvailablePickupOptions && isPickupToggleEnabled ? (
        <PickupOptionsSection
          pickupFulfillmentOptions={pickupFulfillmentOptions}
          isFulfillmentOptionsLoading={isFulfillmentOptionsLoading}
        />
      ) : null}
    </Card>
  );
};
